import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import SEO from '../components/SEO';
import { trackContactFormSubmit } from '../utils/analytics';

const SUBJECTS = [
  { value: 'general',   label: '💬 General question' },
  { value: 'billing',   label: '💳 Billing & payments' },
  { value: 'technical', label: '🔧 Technical issue' },
  { value: 'account',   label: '👤 Account help' },
  { value: 'feature',   label: '✨ Feature request' },
  { value: 'voice',     label: '🎙️ Voice cloning issue' },
  { value: 'other',     label: '📩 Other' },
];

const ContactPage = () => {
  const { user } = useAuth();

  const [form, setForm] = useState({
    name:    user?.name  || '',
    email:   user?.email || '',
    subject: 'general',
    message: '',
  });
  const [loading, setLoading]         = useState(false);
  const [success, setSuccess]         = useState(false);
  const [error, setError]             = useState('');
  const [attachments, setAttachments] = useState([]); // [{ file, preview }]
  const fileInputRef = useRef(null);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    setAttachments(prev => {
      const slots = 3 - prev.length;
      if (slots <= 0) return prev;
      const added = incoming.slice(0, slots).map(file => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      return [...prev, ...added];
    });
    // Reset input so the same file can be re-selected after removal
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name',    form.name);
      fd.append('email',   form.email);
      fd.append('subject', form.subject);
      fd.append('message', form.message);
      attachments.forEach(a => fd.append('attachments', a.file));

      await API.post('/contact', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      trackContactFormSubmit(form.subject);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="Contact Us"
        description="Get in touch with the Dreamy Bedtime team. We're here to help with billing, technical issues, or any questions about your account."
        canonical="/contact"
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-dream text-white" style={{ padding: '56px 0 40px' }}>
        <div className="container text-center" style={{ maxWidth: '600px' }}>
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill">💌 Contact Us</span>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,44px)' }}>
            We'd love to hear from you
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '17px' }}>
            Question, issue, or idea? We're a small team and we actually respond — usually within 24 hours.
          </p>
        </div>
      </section>

      <div className="container py-5" style={{ maxWidth: '720px' }}>

        {success ? (
          /* ── Success state ── */
          <div className="card border-0 shadow-sm text-center p-5" style={{ borderRadius: '24px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
            <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
              Message received!
            </h4>
            <p className="text-muted mb-4" style={{ fontSize: '16px', maxWidth: '420px', margin: '0 auto 24px' }}>
              Thanks for reaching out. We'll get back to you at <strong>{form.email}</strong> within 24–48 hours.
            </p>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <Link to="/" className="btn btn-dream rounded-pill px-5 fw-semibold">
                Back to Home
              </Link>
              <button
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={() => { setSuccess(false); setForm(f => ({ ...f, message: '' })); }}
              >
                Send another message
              </button>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {/* ── Contact form ── */}
            <div className="col-lg-7">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '24px' }}>
                <div className="card-body p-4 p-lg-5">
                  <h5 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '22px' }}>
                    Send us a message
                  </h5>

                  {error && (
                    <div className="alert alert-danger py-2 mb-4 small rounded-3">
                      <i className="bi bi-exclamation-circle me-2"></i>{error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 mb-3">
                      <div className="col-sm-6">
                        <label className="form-label fw-semibold small">
                          Your name <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          placeholder="e.g. Sarah"
                          value={form.name}
                          onChange={e => set('name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-sm-6">
                        <label className="form-label fw-semibold small">
                          Email address <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          type="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={e => set('email', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold small">What's this about?</label>
                      <div className="d-flex flex-wrap gap-2">
                        {SUBJECTS.map(s => (
                          <button
                            key={s.value}
                            type="button"
                            className={`btn btn-sm rounded-pill ${form.subject === s.value ? 'btn-dream' : 'btn-outline-secondary'}`}
                            onClick={() => set('subject', s.value)}
                            style={{ fontSize: '13px' }}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold small">
                        Message <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={5}
                        placeholder="Describe your question or issue in as much detail as you can…"
                        value={form.message}
                        onChange={e => set('message', e.target.value)}
                        maxLength={2000}
                        required
                        style={{ resize: 'none', borderRadius: '12px' }}
                      />
                      <div className="text-end text-muted small mt-1">{form.message.length}/2000</div>
                    </div>

                    {/* Attachments */}
                    <div className="mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label fw-semibold small mb-0">
                          Screenshots <span className="text-muted fw-normal">(optional, up to 3)</span>
                        </label>
                        {attachments.length < 3 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                            style={{ fontSize: '12px' }}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <i className="bi bi-image me-1"></i>Add image
                          </button>
                        )}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFiles}
                      />
                      {attachments.length > 0 && (
                        <div className="d-flex gap-2 flex-wrap">
                          {attachments.map((a, i) => (
                            <div key={i} style={{ position: 'relative', width: '88px', height: '88px' }}>
                              <img
                                src={a.preview}
                                alt={`attachment ${i + 1}`}
                                style={{
                                  width: '88px', height: '88px',
                                  objectFit: 'cover', borderRadius: '10px',
                                  border: '2px solid #e0d4f7', display: 'block',
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeAttachment(i)}
                                style={{
                                  position: 'absolute', top: '-6px', right: '-6px',
                                  width: '22px', height: '22px', borderRadius: '50%',
                                  background: '#6f42c1', color: '#fff', border: 'none',
                                  fontSize: '12px', lineHeight: '22px', textAlign: 'center',
                                  cursor: 'pointer', padding: 0,
                                }}
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {attachments.length < 3 && (
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              style={{
                                width: '88px', height: '88px', borderRadius: '10px',
                                border: '2px dashed #d4b8ff', background: '#faf7ff',
                                color: '#9b7dd4', fontSize: '24px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                              title="Add another image"
                            >
                              +
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-dream btn-lg w-100 rounded-pill fw-bold"
                      disabled={loading}
                    >
                      {loading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
                        : <><i className="bi bi-send me-2"></i>Send Message</>}
                    </button>

                    <p className="text-muted small text-center mt-3 mb-0">
                      <i className="bi bi-shield-check me-1"></i>
                      We never share your email and respond to every message.
                    </p>
                  </form>
                </div>
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="col-lg-5 d-flex flex-column gap-3">

              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚡</div>
                <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Typical response time</h6>
                <p className="text-muted small mb-0">
                  We reply to every message within <strong>24–48 hours</strong>, Monday through Friday.
                  Urgent billing issues are usually resolved same day.
                </p>
              </div>

              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📧</div>
                <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Email us directly</h6>
                <a href="mailto:support@dreamybedtime.com" style={{ color: '#6f42c1', fontWeight: 600, fontSize: '14px' }}>
                  support@dreamybedtime.com
                </a>
                <p className="text-muted small mb-0 mt-1">
                  Same inbox — just a different way to reach us.
                </p>
              </div>

              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔍</div>
                <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Before you write</h6>
                <p className="text-muted small mb-2">
                  Many questions are answered in our FAQ:
                </p>
                <Link to="/faq" style={{ color: '#6f42c1', fontWeight: 600, fontSize: '14px' }}>
                  Browse the FAQ <i className="bi bi-arrow-right ms-1"></i>
                </Link>
              </div>

              {user && (
                <div className="card border-0 p-4" style={{ borderRadius: '20px', background: 'var(--purple-section)', border: '1px solid var(--border-purple2)' }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{ width: '32px', height: '32px', background: '#6f42c1', fontSize: '14px', flexShrink: 0 }}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="fw-bold small" style={{ color: 'var(--text-main)' }}>Logged in as {user.name}</span>
                  </div>
                  <p className="text-muted small mb-0">
                    Your message will be linked to your account so we can help you faster.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="text-center pb-5">
        <p className="text-muted small">
          By submitting this form you agree to our{' '}
          <Link to="/privacy" style={{ color: '#6f42c1' }}>Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
};

export default ContactPage;
